import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Grid,
  Box,
  Typography,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import dayjs from "dayjs";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import FormResponseTable from "../FormResponse/FormResponseTable";
import MyFormFilterDrawer from "./MyFormFilterDrawer";
import NoDataAvailable from "../../../components/NoDataAvailable/NoDataAvailable";
import Loader from "../../../components/Loader/Loader";
import IconButtons from "../../../components/IconButtons/IconButtons";
import UserTableAvatar from "../../../components/UserTableAvatar/UserTableAvatar";

import { calculateFilterCount, font_15, getStatus, ROLE_TYPES } from "../../../utils/utils";
import {
  fill_form_active_icon,
  fill_form_disabled_icon,
  filter_active_icon,
  toast_warning_icon,
} from "../../../constants/icons";
import { HelpTexts } from "../../../constants/constants";
import "./MyForm.scss";

import {
  clearFormResponse,
  fetchFormResponseForResponder,
  fetchNonAttendeesAndProjects,
  fetchResponseListingFilterOptions,
  fillFormResponse,
} from "../../../store/formResponseSlice";
import FormFill from "../FormFill/FormFill";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BootstrapTooltip from "../../../components/BootstrapTooltip/BootstrapTooltip";

dayjs.extend(utc);
dayjs.extend(tz);

const initialFilter = {
  bootcamp: null,
  formName: null,
  trigger: null,
  status: null,
};

const status = {
  statuses: [
    { id: 0, title: "Active", value: "ACTIVE", variant: "GREEN-TEXT" },
    { id: 1, title: "Inactive", value: "INACTIVE", variant: "RED-TEXT" },
    { id: 3, title: "Completed", value: "COMPLETED", variant: "GREEN-TEXT" },
    { id: 4, title: "Pending", value: "PENDING", variant: "RED-TEXT" },
  ],
  getVariant: (value) =>
    status.statuses.find((it) => it.value === value)?.variant || "",
  getTitle: (value) =>
    status.statuses.find((it) => it.value === value)?.title || "",
};

const { NON_ACTIVE_ATTENDEE } = HelpTexts;

const createNameEmailColumn = (attendeeStatuses) => ({
  key: "fullName",
  label: "Name",
  render: (value, row) => (
    <UserTableAvatar
      imgAlt={row.fullName}
      imgSrc={row.profilePictureUrl}
      href={row.newerId}
      label={row.fullName}
      subLabel={row.newerEmail}
      deleted={row.userDeleted}
      userId={row.newerId}
      tooltip={
        row.userDeleted
          ? NON_ACTIVE_ATTENDEE(
            getStatus(attendeeStatuses, row.attendeeStatus),
            dayjs(row.statusUpdatedDate)
              .tz(dayjs.tz.guess())
              .format("DD MMM YYYY")
          )
          : null
      }
    />
  ),
});

const createMentorEmailColumn = () => ({
  key: "mentorEmail",
  label: "Mentor",
  render: (value, row) => (
    <UserTableAvatar
      imgAlt={row.mentorFullName}
      imgSrc={row.mentorProfilePictureUrl}
      href={row.mentorId}
      label={row.mentorFullName}
      subLabel={row.mentorEmail}
      deleted={row.mentorDeleted}
      userId={row.mentorId}
    />
  ),
});

const createEvaluatedByEmailColumn = () => ({
  key: "evaluatedBy",
  label: "Evaluated By",
  render: (value, row) => (
    <UserTableAvatar
      imgAlt={row.evaluatedby}
      imgSrc={row.evaluatedbyProfilePictureUrl}
      href={row.evaluatedbyId}
      label={row.evaluatedby}
      subLabel={row.evaluatedbyEmail}
      deleted={row.evaluatedbyDeleted}
      userId={row.evaluatedbyId}
    />
  ),
});

const createReportingManagerColumn = () => ({
  key: "reportingManagerEmail",
  label: "Reporting Manager",
  render: (value, row) => (
    <UserTableAvatar
      imgAlt={row.reportingManagerFullName}
      imgSrc={row.reportingManagerProfilePictureUrl}
      href={row.reportingManagerId}
      label={row.reportingManagerFullName}
      subLabel={row.reportingManagerEmail}
      deleted={row.reportingManagerDeleted}
      userId={row.reportingManagerId}
    />
  ),
});

const createStatusColumn = (key, label) => ({
  key: key,
  label: label,
  minWidth: 150,
  maxWidth: 150,
  render: (value) => (
    <Box className="empoyee-status">
      <Chip
        className="same-size-chip"
        variant={status.getVariant(value)}
        label={status.getTitle(value)}
      />
    </Box>
  ),
});

function prepareBootcampOptions(bootcamps) {
  if (!Array.isArray(bootcamps)) return [];
  return bootcamps.map(({ id, name }) => ({ id, name }));
}

function prepareFormOptions(forms) {
  if (!Array.isArray(forms)) return [];
  return forms.map(({ id, label, type, description, allowResponseModify, triggers }) => ({
    id,
    name: label,
    type,
    description: description || "",
    allowResponseModify: allowResponseModify || false,
    triggers: triggers || []
  }));
}

function prepareFormStatus(status) {
  if (!Array.isArray(status)) return [];
  return status.map((el) => ({ id: el.key, name: el.value }));
}

const formatDate = (date) => {
  if (!date) return "-";
  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) return "-";
  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const extractUserFields = (user = {}) => ({
  newerId: user.employeeCode || "",
  fullName: user.fullName || "",
  profilePictureUrl: user.profilePictureUrl || "",
  newerEmail: user.email || "",
  userDeleted: user.deleted || false,
  competency: user.competency?.name || "",
  employeeStatus: user.deleted ? "INACTIVE" : "ACTIVE",
  allocatedProjects: user.allocatedProjects.results,
});

const extractMentorFields = (mentor = {}) => ({
  mentorFullName: mentor.fullName || "",
  mentorEmail: mentor.email || "",
  mentorProfilePictureUrl: mentor.profilePictureUrl || "",
  mentorDeleted: mentor.deleted || false,
  mentorId: mentor.employeeCode,
});

const extractReportingManagerFields = (reportingManager = {}) => ({
  reportingManagerFullName: reportingManager.fullName || "",
  reportingManagerEmail: reportingManager.email || "",
  reportingManagerProfilePictureUrl: reportingManager.profilePictureUrl || "",
  reportingManagerDeleted: reportingManager.deleted || false,
  reportingManagerId: reportingManager.employeeCode,
});

const extractEvaluatedByFields = (evaluatedby = {}) => ({
  evaluatedby: evaluatedby?.fullName || "",
  evaluatedbyEmail: evaluatedby?.email || "",
  evaluatedbyProfilePictureUrl: evaluatedby?.profilePictureUrl || "",
  evaluatedbyDeleted: evaluatedby?.deleted || false,
  evaluatedbyId: evaluatedby?.employeeCode,
});

const extractUserData = (user = {}) => {
  const mentor = user.mentor || {};
  const reportingManager = user.reportingManager || {};
  return {
    ...extractUserFields(user),
    ...extractMentorFields(mentor),
    ...extractReportingManagerFields(reportingManager),
  };
};

const extractAssessmentData = (item = {}) => ({
  dateOfAssessment: formatDate(item.trigger?.assessmentDate),
  assessmentPeriod: `${formatDate(item.trigger?.periodFrom)} - ${formatDate(
    item.trigger?.periodTo
  )}`,
  assessmentName: item.trigger?.assessmentName,
  bootcamp: item.trigger?.bootcamp.name,
  status: item.status,
});

const buildDynamicColumnsData = (inputs = []) =>
  inputs.reduce((acc, input) => {
    const label = input?.input?.label || "Unknown";
    acc[label] = {
      value: (input?.value || []).join(", "),
      tooltip: input?.input?.description || "",
    };
    return acc;
  }, {});

const mapApiToTableRows = (apiResponse = []) =>
  apiResponse.map((item) => ({
    ...extractUserData(item.filledForUser),
    ...extractAssessmentData(item),
    ...buildDynamicColumnsData(item.inputResponses?.results),
    ...extractEvaluatedByFields(item.filledByUser),
    original: item,
    actions: "",
  }));


const getScoreClass = (val) => {
  const score = parseFloat(val);
  if (isNaN(score)) return null;
  if (score >= 0 && score <= 2.5) return "score-chip chip-red";
  if (score > 2.5 && score < 3.8) return "score-chip chip-yellow";
  if (score >= 3.8) return "score-chip chip-green";
  return "score-chip chip-default";
};

const buildDynamicColumns = (apiData = []) => {
  const columnMap = new Map();

  apiData.forEach(item => {
    item?.inputResponses?.results?.forEach(input => {
      const label = input?.input?.label;

      if (!label || columnMap.has(label)) return;

      const isAverageScore = input?.input?.type === "AVERAGE_SCORE";

      columnMap.set(label, {
        key: label,
        label,
        tooltip: input?.input?.description || "",
        render: (cell) => {
          if (isAverageScore && cell?.value && cell.value !== "-") {
            const className = getScoreClass(cell.value);
            if (className) {
              return (
                <Chip
                  label={cell.value}
                  size="small"
                  className={className}
                />
              );
            }
          }
          return (
            <Box title={cell?.tooltip || ""}>
              {cell?.value || "-"}
            </Box>
          );
        },
      });
    });
  });

  return Array.from(columnMap.values());
};

function MyForm() {
  const dispatch = useDispatch();
  const isInitialized = useRef(false);

  const {
    list: apiData = [],
    loading = false,
    totalResults = 0,
    filterOptions: apiFilters = {},
  } = useSelector((state) => state.formResponse || {});

  const { nonAttendeesAndProjects } = useSelector(
    (state) => state.formResponse
  );
  const attendeeStatuses = useSelector(
    (state) => state?.utility?.constants?.attendeeStatuses
  );

  const security = useSelector((state) => state?.security);
  const formStatusList = useSelector((state) => state.utility.constants?.formStatus);
  const [openFilter, setOpenFilter] = useState(false);
  const [isFilter, setIsFilter] = useState(initialFilter);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [bootcampOptions, setBootCampOptions] = useState([]);
  const [formNameOptions, setFormNameOptions] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [formfields, setFormFields] = useState([]);
  const [formResponseId, setFormResponseId] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [formStatus, setFormStatus] = useState([]);

  const [unsavedWarningOpen, setUnsavedWarningOpen] = useState(false);
  const [attemptedClose, setAttemptedClose] = useState(false);
  const [selectedFormType, setSelectedFormType] = useState("");
  const [allocatedProjects, setAllocatedProjects] = useState([]);


  const prepareSelectedProjects = (projects = []) =>
    projects.map((project) => ({
      label: project?.name,
      value: project?.name,
      subLabel: project?.businessUnit,
    }));

  const handleFormModal = (row) => {
    const selecatedProjects = prepareSelectedProjects(row?.allocatedProjects);
    setAllocatedProjects(selecatedProjects || []);
    setOpenForm(true);
    setFormResponseId(row.original.id);
    const numericFieldDataUpdate = row.original.inputResponses.results.map(
      (el) => {
        const field = el.input;
        const value = el.value;
        let numericValue;
        if (field.type === "NUMERIC_TEXT") {
          if (value.length != 0) {
            numericValue = Number(value);
          } else if (field?.defaultValue) {
            numericValue = Number(field?.defaultValue);
          }
          if (Number.isNaN(numericValue)) {
            return el;
          }

          return {
            ...el,
            value: numericValue,
          };
        }

        return el;
      }
    );

    setFormFields(numericFieldDataUpdate);
    setUserDetails({ fullName: row.fullName, bootcamp: row.bootcamp, description: isFilter?.formName?.description || "" });
  };

  const getPermissions = (row) => {
    const { loggedInUser, roles } = security || {};
    const loggedInUserId = loggedInUser?.id;

    if (!loggedInUserId) {
      return { edit: false, reason: "User information not available" };
    }

    const { original } = row;
    const filledByUserId = original?.filledByUser?.id;

    if (filledByUserId && filledByUserId !== loggedInUserId) {
      return { edit: false, reason: "Only the assigned evaluator can fill this form" };
    }

    if (!filledByUserId) {
      const bootcamp = original?.trigger?.bootcamp;
      if (!bootcamp) {
        return { edit: false, reason: "Bootcamp information is missing" };
      }

      const spocs = bootcamp?.spocs?.results || [];
      const userCompetencyId = loggedInUser?.competency?.id;
      const bootcampCompetencyId = bootcamp?.competency?.id;

      const isAssignedSpoc = spocs.some((spoc) => spoc.id === loggedInUserId);
      const isMatchingCompetencySpoc =
        ROLE_TYPES.isCompetencySpoc(roles) &&
        bootcampCompetencyId === userCompetencyId;

      if (!isAssignedSpoc && !isMatchingCompetencySpoc) {
        return { edit: false, reason: "You are not assigned as a SPOC for this bootcamp" };
      }
    }

    if (row.status === "COMPLETED") {
      const targetForm = formNameOptions.find((f) => f.id === original?.formId);
      if (!targetForm?.allowResponseModify) {
        return { edit: false, reason: "This form is completed and cannot be modified" };
      }
    }

    return { edit: true, reason: null };
  };

  const rows = useMemo(() => {
    const dataToMap = Array.isArray(apiData) ? apiData : apiData?.results || [];
    return mapApiToTableRows(dataToMap);
  }, [apiData]);

  const columns = useMemo(() => {
    const currentType = selectedFormType;
    const staticColumns = [
      createNameEmailColumn(attendeeStatuses),
      createMentorEmailColumn(),
      createReportingManagerColumn(),
      createEvaluatedByEmailColumn(),
      createStatusColumn("status", "Status"),
      {
        key: "actions",
        label: "Action",
        minWidth: 100,
        render: (value, row) => {
          const { edit, reason } = getPermissions(row);
          return (
            <IconButtons
              image={edit ? fill_form_active_icon : fill_form_disabled_icon}
              tooltip={edit ? "Fill Feedback" : (reason || "You don't have permission to fill this form")}
              handleClick={() => edit && handleFormModal(row)}
              width={font_15}
              height={font_15}
            />
          );
        }
      },
      { key: "competency", label: "Competency", minWidth: 120, maxWidth: 120 },
      { key: "bootcamp", label: "Bootcamp" },
    ];
    if (currentType === "CLASSROOM_ASSESSMENT_PERFORMANCE") {
      staticColumns.push(
        { key: "assessmentName", label: "Assessment Name" },
        { key: "dateOfAssessment", label: "Date of Assessment" }
      );
    } else {
      staticColumns.push({
        key: "assessmentPeriod",
        label: "Assessment Period",
      });
    }

    const dynamicColumns = buildDynamicColumns(apiData?.results);

    return [...staticColumns, ...dynamicColumns];
  }, [attendeeStatuses, apiData, selectedFormType, security, formNameOptions]);

  const filterCount = useMemo(() => calculateFilterCount(isFilter), [isFilter]);
  const filterActivated = filterCount > 0;
  const totalPages = useMemo(
    () => Math.ceil(totalResults / 20),
    [totalResults]
  );

  const applyFilter = useCallback(
    (filter = isFilter, pageOffset = offset) => {
      if (!filter?.formName?.id) return;
      dispatch(
        fetchFormResponseForResponder({
          formId: filter.formName.id,
          triggerId: filter.trigger?.id || null,
          status: filter?.status?.id || null,
          offset: pageOffset,
        })
      );
      dispatch(clearFormResponse());
      dispatch(fetchNonAttendeesAndProjects({ formId: filter.formName.id }));
    },
    [dispatch, isFilter, offset]
  );

  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const loadFilterOptions = async () => {
      const resultAction = await dispatch(
        fetchResponseListingFilterOptions()
      ).unwrap();

      const { form, bootcamps } = resultAction || {};
      const formOptions = prepareFormOptions(form?.results);
      const bOptions = prepareBootcampOptions(bootcamps?.results);
      const formStatus = prepareFormStatus(formStatusList)

      setFormStatus(formStatus);
      setFormNameOptions(formOptions);
      setBootCampOptions(bOptions);

      if (formOptions.length) {
        const defaultFilter = { ...initialFilter, formName: formOptions[0] };
        setIsFilter(defaultFilter);
        setSelectedFormType(defaultFilter?.formName?.type);
        applyFilter(defaultFilter, 0);
      }
    };
    loadFilterOptions();
  }, [dispatch, applyFilter]);

  useEffect(() => {
    return () => {
      dispatch(clearFormResponse());
    };
  }, [dispatch]);

  const handleFilterChange = (name, value) => {
    setIsFilter((prev) => {
      const newFilter = { ...prev, [name]: value };

      if (name === "formName") {
        newFilter.trigger = null;
      }

      return newFilter;
    });
  };

  const handleFilterSubmit = () => {
    setOffset(0);
    setCurrentPage(1);
    setOpenFilter(false);
    setSelectedFormType(isFilter?.formName?.type);
    applyFilter(isFilter, 0);
  };

  const handleFilterRemove = () => {
    const firstForm = apiFilters?.form?.results?.[0];
    if (!firstForm) return;

    const resetFilter = {
      bootcamp: null,
      formName: {
        id: firstForm.id,
        name: firstForm.label,
        type: firstForm.type,
      },
      trigger: null,
      status: null,
    };

    setIsFilter(resetFilter);
    setSelectedFormType(resetFilter.formName.type);
    setOffset(0);
    setCurrentPage(1);
    setOpenFilter(false);
    applyFilter(resetFilter, 0);
  };

  const handlePageChange = (page) => {
    const newOffset = page - 1;
    setCurrentPage(page);
    setOffset(newOffset);
    applyFilter(isFilter, newOffset);
  };

  const handleFormSubmit = async (data) => {
    await dispatch(
      fillFormResponse({
        formDetails: data,
      })
    ).unwrap();

    applyFilter(isFilter, offset);

    setOpenForm(false);
  };

  return (
    <>
      {loading && <Loader />}
      <Grid container className="main-container">
        
        <Grid 
          item 
          md={12} 
          className="header-container" 
          sx={{ 
            height: "auto !important",
            minHeight: "60px",
          }}
        >        
          <Box
            display="flex"
            alignItems="flex-start" 
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="font_21_bold">
                {isFilter?.formName?.name || "As Responder"}
              </Typography>

              {isFilter?.formName?.description && (
                <Box display="flex" alignItems="flex-start" gap={0.5} sx={{ mt: 0.2 }}>
                  <InfoOutlinedIcon 
                    sx={{ 
                      fontSize: "1rem", 
                      color: "#de1186", 
                      mt: "3px",
                      flexShrink: 0
                    }} 
                  />
                  <Typography
                    variant="font_12"
                    component="div"
                    sx={{ 
                      color: "#4b4b4b", 
                      fontStyle: "italic",
                      wordBreak: "break-word",
                      "& p": { margin: 0 } 
                    }}
                    dangerouslySetInnerHTML={{
                      __html: isFilter.formName.description,
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box display="flex" alignItems="center" sx={{ alignSelf: "flex-start", mt: 0.5 }}>
              <Box
                className="filterButton"
                sx={{
                  border: filterActivated
                    ? "2px solid #de1186"
                    : "1px solid #de118680",
                }}
              >
                <IconButtons
                  height={font_15}
                  width={font_15}
                  image={filter_active_icon}
                  handleClick={() => setOpenFilter(true)}
                  tooltip="Add Filters"
                  count={filterCount}
                />
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item md={12} className="body-container">
          <Grid container className="table-parent-container">
            <Paper
              elevation={0}
              className="flex-container-column topic-paper-container"
            >
              {!rows.length ? (
                <Grid container className="no-data-container">
                  <NoDataAvailable
                    imgHeight={4}
                    imgWidth={4.5}
                    message="No Data Available"
                  />
                </Grid>
              ) : (
                <FormResponseTable
                  title="Responses"
                  columns={columns}
                  rows={rows}
                  totalResult={totalResults}
                  totalPages={totalPages}
                  currentPage={currentPage}
                  handlePageChange={handlePageChange}
                  fixedColumnsCount={0}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      <MyFormFilterDrawer
        open={openFilter}
        onClose={() => setOpenFilter(false)}
        handleFilterChange={handleFilterChange}
        handleFilterSubmit={handleFilterSubmit}
        handleFilterRemove={handleFilterRemove}
        filterValue={isFilter}
        formNameOptions={formNameOptions}
        statusOptions={formStatus}
        showTriggerFilter={true}
      />
      <FormFill
        open={openForm}
        handleClose={() => setOpenForm(false)}
        formFields={formfields}
        formResponseId={formResponseId}
        selectedFormType={selectedFormType}
        userDetails={userDetails}
        projectsList={nonAttendeesAndProjects?.projects?.results ?? []}
        preSelectedProjects={allocatedProjects}
        usersList={nonAttendeesAndProjects?.users?.results ?? []}
        onSubmit={(values) => handleFormSubmit(values)}
        onAttemptClose={() => {
          setAttemptedClose(true);
          setUnsavedWarningOpen(true);
        }}
      />
      <Dialog
        open={unsavedWarningOpen}
        onClose={() => setUnsavedWarningOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1.25rem" }}>
          Unsaved Changes
        </DialogTitle>

        <DialogContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <img
              src={toast_warning_icon}
              alt="Warning"
              width={24}
              height={24}
            />
            <Typography sx={{ color: "#4b4b4b" }}>
              You have unsaved changes. If you close now, your changes will be
              lost. Are you sure?
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: "0rem 1.5rem 1.875rem" }}>
          <Button
            variant="outlined"
            color="secondary"
            className="block-button"
            onClick={() => setUnsavedWarningOpen(false)}
          >
            <Typography variant="outlineBtnLabel">Cancel</Typography>
          </Button>

          <Button
            variant="contained"
            color="secondary"
            className="block-button"
            onClick={() => {
              setUnsavedWarningOpen(false);
              setOpenForm(false);
            }}
          >
            <Typography variant="outlineBtnLabel"> Close Anyway</Typography>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MyForm;